'use client';

import type { SidebarPanelContent } from '@/atoms/sidebarPanelAtoms';
import { IS_INSET_DESIGN_ENABLED } from '@/features/flags/legacy-flags';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { isTauri } from '@/tauri/utils';
import { PageShell } from '@heidi/design-system/page-shell';
import { skipLinkTargetProps } from '@heidi/design-system/skip-link';
import { memo, useEffect, useMemo, useRef } from 'react';
import { PanelHeader } from './components/panel-header';
import { useSidebarPanel } from './hooks/use-sidebar-panel';
import { EvidenceContentLoader } from './panels/evidence/evidence-content-loader';
import { SessionsContentLoader } from './panels/sessions/sessions-content-loader';
import type { SidebarPanelProps } from './sidebar-panel.types';

/**
 * Secondary sidebar panel with CSS-only mode switching.
 *
 * The panel stays as a single DOM instance and uses CSS to control behavior:
 * - **Pinned (isPinned=true)**: `position: relative`, takes space in document flow
 * - **Floating (isPinned=false)**: viewport-fixed by default, shell-absolute when opted in
 *
 * This approach eliminates remounting on mode toggle, preventing content jank.
 *
 * Features:
 * - Instant show on hover (0ms delay)
 * - 150ms debounce on hide to prevent flicker
 * - Pin/unpin to toggle between temporary and permanent
 * - Desktop only (returns null on mobile)
 * - Respects prefers-reduced-motion
 */
export const SidebarPanel = memo(function SidebarPanel({
  primarySidebarWidth,
  isShellAnchored = false,
}: SidebarPanelProps) {
  const isMobile = useIsMobile();
  const isTauriDesktop = useMemo(() => isTauri(), []);
  const isInsetDesignEnabled = IS_INSET_DESIGN_ENABLED();
  const prevContentRef = useRef<SidebarPanelContent>(null);
  const {
    content,
    isVisible,
    isEnabled,
    isPinned,
    shouldRenderPinned,
    selectedContent,
    togglePin,
    sidebarRef,
    handleSidebarMouseEnter,
    handleSidebarMouseLeave,
    handleSidebarBlur,
  } = useSidebarPanel();

  // Detect content switch (not opening/closing) to skip animations.
  // Note: isSwitching intentionally reflects the PREVIOUS render's state because
  // prevContentRef is updated in useEffect (after render). This one-render-behind
  // pattern ensures isSwitching=true during the render where content actually changes,
  // allowing CSS to skip entry animations when switching between content types.
  const isSwitching =
    prevContentRef.current !== null &&
    content !== null &&
    prevContentRef.current !== content;

  // Update previous content ref after render completes
  useEffect(() => {
    prevContentRef.current = content;
  }, [content]);

  // Don't render on mobile or if feature is disabled
  if (isMobile || !isEnabled) {
    return null;
  }

  // Determine panel state based on visibility
  const panelState = isVisible ? 'open' : 'closed';

  // Human-readable label for screen reader announcement
  const contentLabel =
    content === 'sessions'
      ? 'Sessions panel'
      : content === 'evidence'
        ? 'Evidence panel'
        : null;

  return (
    <PageShell.Panel
      {...skipLinkTargetProps('page-shell-panel')}
      ref={sidebarRef}
      isPinned={shouldRenderPinned}
      state={panelState}
      switching={isSwitching}
      className={cn(
        // Match the primary sidebar's inset vertical padding
        isInsetDesignEnabled && 'py-1',
        // In newBrandDark + inset the panel drops to the deep page tone so it
        // matches the chat canvas contrast model (DS-882). Gated on inset so
        // non-inset newBrandDark keeps painting the chrome-toned bg-surface-accent
        // it always has.
        isInsetDesignEnabled && 'newBrandDark:bg-surface-page',
        // The relative MenuCoachmarkWelcome wrapper stretches with the shell
        // row and already sits beside the primary rail. Use that boundary so
        // both panels clear any banner/title bar above the row without offsets.
        !shouldRenderPinned && isShellAnchored && 'absolute inset-y-0 start-0 h-auto',
        !shouldRenderPinned &&
          !isShellAnchored &&
          isTauriDesktop &&
          // Floating panel should clear only the Tauri desktop header, not SideNav's
          // internal extra top padding used for traffic lights alignment. Height comes
          // from --app-titlebar-height (published by TauriTitlebarHeightVar).
          'top-[var(--app-titlebar-height,0px)] h-[calc(100dvh-var(--app-titlebar-height,0px))]'
      )}
      onMouseEnter={handleSidebarMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
      onBlur={handleSidebarBlur}
      style={
        !shouldRenderPinned && !isShellAnchored
          ? { left: primarySidebarWidth }
          : undefined
      }
      {...(contentLabel && { 'aria-label': contentLabel })}
    >
      {/* Panel header with create action and pin toggle */}
      <PanelHeader
        content={content}
        isPinned={isPinned}
        onTogglePin={togglePin}
        showPinToggle={selectedContent !== null && content === selectedContent}
      />

      {/* Inner container - content manages its own scrolling */}
      <div className="scrollbar-panels flex min-h-0 flex-1 flex-col overflow-hidden py-2">
        {/* Both content types are always mounted, visibility controlled by CSS */}
        <div
          className={cn('h-full', content !== 'sessions' && 'hidden')}
          aria-hidden={content !== 'sessions'}
        >
          <SessionsContentLoader />
        </div>
        <div
          className={cn('h-full', content !== 'evidence' && 'hidden')}
          aria-hidden={content !== 'evidence'}
        >
          <EvidenceContentLoader />
        </div>
      </div>
    </PageShell.Panel>
  );
});
